import type { CSSProperties } from 'react';
import { getHazardIconUrl } from '../Home/map/hazards/hazardIcons';
import type { LaneEvent } from '../Home/map/laneEvents';
import { formatBoardTime } from './arrivalRows';
import { formatKm } from './tripMath';

const describeWhere = (event: LaneEvent, locale: string): string | null => {
  if (event.where === 'here') return 'Where the shipment is now';
  if (event.where === 'destination') return 'Near destination';
  const distance = formatKm(event.aheadKm, locale);
  return distance ? `${distance} ahead` : null;
};

/** "Starts Tue 19:00" for an upcoming event, else its time window. */
const describeWindow = (event: LaneEvent, now: number): string =>
  event.startsAt
    ? `Starts ${formatBoardTime(event.startsAt, new Date(now))}`
    : [event.effective, event.expiration].filter(Boolean).join(' – ');

/**
 * "On this route": the weather and non-weather events on the lane ahead of
 * the shipment in focus. The map's event cards open on hover, which a wall
 * TV never gets, so the screen names them here.
 */
const LaneEventList = ({
  events,
  now,
}: {
  events: LaneEvent[];
  now: number;
}) => {
  if (events.length === 0) return null;
  return (
    <div className='monitor-lane' aria-label='On this route'>
      <span className='monitor-trip-label'>On this route</span>
      {events.map((event) => {
        // The headline says what happened ("Port strike at Felixstowe"); the
        // type is context once there is one.
        const headline = event.title || event.typeLabel;
        const typeContext = headline !== event.typeLabel ? event.typeLabel : '';
        return (
          <div key={event.key} className='monitor-lane-event'>
            <img src={getHazardIconUrl(event.iconKey)} alt='' />
            <div className='monitor-lane-text'>
              <div className='monitor-lane-title' title={headline}>
                {headline}
              </div>
              <div className='monitor-lane-detail'>
                <span
                  className='monitor-lane-risk'
                  style={
                    {
                      '--monitor-lane-risk-color': event.severityColor,
                    } as CSSProperties
                  }
                >
                  {event.severityLabel}
                </span>
                <span>
                  {[
                    typeContext,
                    describeWhere(event, navigator.language),
                    describeWindow(event, now),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default LaneEventList;
